/*!
 * BOPF Hermes page live feeds
 * Pulls Birkin/Kelly products from /collections/the-bopf-hermes-edit
 * and /collections/recently-sold via Shopify's public products.json,
 * renders into [data-bopf-edit-grid] and [data-bopf-sold-rows].
 * Fails silent and keeps static fallback content on any error.
 *
 * Install:
 *   1. Save this file to your theme as: assets/bopf-hermes-feeds.js
 *   2. Add to theme.liquid (just before </body>):
 *      {% if page.handle == 'hermes' %}
 *        <script src="{{ 'bopf-hermes-feeds.js' | asset_url }}" defer></script>
 *      {% endif %}
 */

console.log('[BOPF] script tag parsed, document.readyState =', document.readyState);
(function () {
  'use strict';

  var SHOP_URL = 'https://businessofprelovedfashion.com';
  var TITLE_FILTER = /birkin|kelly/i;
  var EDIT_LIMIT = 4;
  var SOLD_LIMIT = 4;
  var FETCH_LIMIT = 50;
  var JUST_LISTED_DAYS = 7;
  var CONDITION_TAGS = ['New', 'Unworn', 'Excellent', 'Very Good', 'Good', 'Loved'];
  var CURRENCY = 'AED';
  var MIN_PRICE = 80000; // AED minimum to qualify for the edit grid

  // ----- Helpers -----
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatPrice(amount, currency) {
    var n = parseFloat(amount);
    if (isNaN(n)) return '';
    return currency + ' ' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  function listedAgo(dateStr) {
    var d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    var days = Math.floor((new Date() - d) / 86400000);
    if (days <= 0) return 'Listed today';
    if (days === 1) return 'Listed yesterday';
    if (days < 7) return 'Listed ' + days + ' days ago';
    if (days < 14) return 'Listed last week';
    var weeks = Math.floor(days / 7);
    if (weeks < 5) return 'Listed ' + weeks + ' weeks ago';
    var months = Math.floor(days / 30);
    return 'Listed ' + months + (months === 1 ? ' month ago' : ' months ago');
  }

  function relativeTime(dateStr) {
    var d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    var days = Math.floor(Math.max(0, new Date() - d) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return '1 day';
    if (days < 7) return days + ' days';
    var weeks = Math.floor(days / 7);
    if (weeks === 1) return '1 week';
    if (weeks < 5) return weeks + ' weeks';
    var months = Math.floor(days / 30);
    if (months === 1) return '1 month';
    if (months < 12) return months + ' months';
    var years = Math.floor(days / 365);
    return years === 1 ? '1 year' : years + ' years';
  }

  function parseTitle(fullTitle) {
    var t = String(fullTitle || '').replace(/^(hermès|hermes)\s+/i, '').trim();
    var m = t.match(/^((?:Kelly|Birkin|Constance|Lindy|Picotin|Evelyne|Garden Party|Bolide|Halzan|Verrou|Berline|Roulis|Toolbox)\s+\d+\s*(?:Sellier|Retourne|Mini|HSS)?)/i);
    if (m) {
      return {
        title: m[1].replace(/\s+/g, ' ').trim(),
        spec: t.substring(m[1].length).replace(/^[\s,.\-:]+/, '').replace(/^(in|with)\s+/i, '').trim()
      };
    }
    return { title: t, spec: '' };
  }

  function findCondition(tags) {
    // products.json may return tags as an array or as a comma-separated string.
    var arr = Array.isArray(tags)
      ? tags
      : (typeof tags === 'string' ? tags.split(',').map(function (s) { return s.trim(); }) : []);
    for (var i = 0; i < arr.length; i++) {
      for (var j = 0; j < CONDITION_TAGS.length; j++) {
        if (String(arr[i]).toLowerCase() === CONDITION_TAGS[j].toLowerCase()) {
          return CONDITION_TAGS[j];
        }
      }
    }
    return null;
  }

  function isAvailable(p) {
    return Array.isArray(p.variants) && p.variants.some(function (v) { return v && v.available; });
  }

  function fetchCollection(handle, limit) {
    var url = SHOP_URL + '/collections/' + handle + '/products.json?limit=' + (limit || FETCH_LIMIT);
    return fetch(url, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status + ' from ' + url);
        return r.json();
      })
      .then(function (data) {
        if (!data || !Array.isArray(data.products)) {
          throw new Error('Unexpected payload shape from ' + url);
        }
        return data.products;
      });
  }

  // ============================================================
  // FEED 1: Live edit grid
  // Combines current Hermès edit (available) with recently-sold,
  // filters for Hermès brand + Birkin/Kelly title.
  // Available items render normally; sold items get the SOLD overlay.
  // ============================================================
  function start() {
    console.log('[BOPF] start() running, looking for target elements');
    loadEditGrid();
    loadSoldLedger();
  }

  if (document.readyState === 'loading') {
    console.log('[BOPF] DOM still loading, deferring to DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  function loadEditGrid() {
    var grid = document.querySelector('[data-bopf-edit-grid]');
    if (!grid) {
      console.warn('[BOPF] edit grid: [data-bopf-edit-grid] element not found in DOM. The section markup is probably missing or the script is running before the DOM has loaded.');
      return;
    }
    console.log('[BOPF] edit grid: found target element, fetching collections...');

    function isHermesBrand(p) {
      if (p.vendor && /herm[èe]s/i.test(p.vendor)) return true;
      return /herm[èe]s/i.test(p.title);
    }

    function meetsMinPrice(p) {
      if (!Array.isArray(p.variants) || !p.variants.length) return false;
      var price = parseFloat(p.variants[0].price);
      if (isNaN(price)) return false;
      return price >= MIN_PRICE;
    }

    function passesFilters(p) {
      if (!Array.isArray(p.images) || !p.images.length) return false;
      if (!isHermesBrand(p)) return false;
      if (!TITLE_FILTER.test(p.title)) return false;
      if (!meetsMinPrice(p)) return false;
      return true;
    }

    function renderPiece(p, isSold) {
      var parsed = parseTitle(p.title);
      var imgUrl = p.images[0].src;
      var productUrl = '/products/' + p.handle;
      var alt = (p.images[0].alt) ? p.images[0].alt : p.title;

      var leftCell, rightCell, badges = '', overlay = '';

      if (isSold) {
        var soldDate = p.updated_at || p.published_at || p.created_at;
        var rel = relativeTime(soldDate);
        var soldLine = (rel === 'today') ? 'today' : rel + ' ago';
        overlay = '<div class="piece-sold-overlay">Sold</div>';
        leftCell = '<span class="piece-price" style="color: var(--ink-mute);">Sold</span>';
        rightCell = '<span class="piece-listed">' + escapeHtml(soldLine) + '</span>';
      } else {
        var listedDate = p.published_at || p.created_at;
        var daysSince = Math.floor((new Date() - new Date(listedDate)) / 86400000);
        var isJustListed = daysSince < JUST_LISTED_DAYS;
        var price = p.variants && p.variants[0] && p.variants[0].price;
        var priceStr = price ? formatPrice(price, CURRENCY) : '';
        var cond = findCondition(p.tags);

        if (isJustListed) badges += '<span class="badge badge-new">Just listed</span>';
        if (cond) badges += '<span class="badge badge-cond">' + escapeHtml(cond) + '</span>';

        leftCell = '<span class="piece-price">' + escapeHtml(priceStr) + '</span>';
        rightCell = '<span class="piece-listed">' + escapeHtml(listedAgo(listedDate)) + '</span>';
      }

      return (
        '<a class="piece" href="' + escapeHtml(productUrl) + '">' +
          '<div class="piece-img">' +
            '<img src="' + escapeHtml(imgUrl) + '" alt="' + escapeHtml(alt) + '" loading="lazy">' +
            (badges ? '<div class="piece-badges">' + badges + '</div>' : '') +
            overlay +
          '</div>' +
          '<div class="piece-body">' +
            '<p class="piece-title">' + escapeHtml(parsed.title) + '</p>' +
            (parsed.spec ? '<p class="piece-spec">' + escapeHtml(parsed.spec) + '</p>' : '') +
            '<div class="piece-row">' +
              leftCell +
              rightCell +
            '</div>' +
          '</div>' +
        '</a>'
      );
    }

    Promise.all([
      fetchCollection('the-bopf-hermes-edit').catch(function (e) {
        console.warn('[BOPF] edit grid: hermes-edit fetch failed', e);
        return [];
      }),
      fetchCollection('recently-sold').catch(function (e) {
        console.warn('[BOPF] edit grid: recently-sold fetch failed', e);
        return [];
      })
    ]).then(function (results) {
      var availableSource = results[0] || [];
      var soldSource = results[1] || [];

      console.info('[BOPF] edit grid: ' + availableSource.length + ' from hermes-edit, ' + soldSource.length + ' from recently-sold');

      var availableMatches = availableSource.filter(function (p) {
        return passesFilters(p) && isAvailable(p);
      });
      var soldMatches = soldSource.filter(passesFilters);

      console.info('[BOPF] edit grid: ' + availableMatches.length + ' available + ' + soldMatches.length + ' sold = ' + (availableMatches.length + soldMatches.length) + ' total Birkin/Kelly matches at AED ' + MIN_PRICE + '+');

      // Available first, sold filling out the row
      var combined = [];
      availableMatches.forEach(function (p) { combined.push({ p: p, sold: false }); });
      soldMatches.forEach(function (p) { combined.push({ p: p, sold: true }); });
      combined = combined.slice(0, EDIT_LIMIT);

      if (!combined.length) {
        console.warn('[BOPF] edit grid: no matches anywhere, keeping fallback');
        return;
      }

      grid.innerHTML = combined.map(function (entry) {
        return renderPiece(entry.p, entry.sold);
      }).join('');
    });
  }

  // ============================================================
  // FEED 2: Recently sold ledger (Birkin/Kelly only)
  // ============================================================
  function loadSoldLedger() {
    var rows = document.querySelector('[data-bopf-sold-rows]');
    if (!rows) {
      console.warn('[BOPF] sold ledger: [data-bopf-sold-rows] element not found in DOM.');
      return;
    }
    console.log('[BOPF] sold ledger: found target element, fetching recently-sold...');

    fetchCollection('recently-sold')
      .then(function (products) {
        console.info('[BOPF] sold ledger: fetched ' + products.length + ' products from recently-sold');

        var matches = products
          .filter(function (p) { return TITLE_FILTER.test(p.title); })
          .slice(0, SOLD_LIMIT);

        console.info('[BOPF] sold ledger: ' + matches.length + ' Birkin/Kelly matches');

        if (!matches.length) {
          console.warn('[BOPF] sold ledger: no matches, keeping fallback');
          return;
        }

        rows.innerHTML = matches.map(function (p) {
          var cleaned = String(p.title).replace(/^(hermès|hermes)\s+/i, '').trim();
          var dateRef = p.updated_at || p.published_at || p.created_at;
          return (
            '<div class="ledger-row">' +
              '<p class="ledger-piece">' + escapeHtml(cleaned) + '</p>' +
              '<span class="ledger-time">' + escapeHtml(relativeTime(dateRef)) + '</span>' +
            '</div>'
          );
        }).join('');
      })
      .catch(function (err) {
        console.warn('[BOPF] sold ledger: fetch failed, keeping fallback', err);
      });
  }

})();